// ============================================================
// StepAcademic Component
// ============================================================

import { 
  TextField, 
  MenuItem, 
  Grid, 
  CircularProgress, 
  FormControlLabel, 
  Checkbox, 
  Box, 
  Link, 
  Autocomplete,
  Typography,
  Chip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import studentService from '@/services/studentService';
import academicSessionService from '@/services/academicSessionService';
import { useSchool } from '@/hooks/useSchool';
import { useAuth } from '@/hooks/useAuth';
import { ROLES } from '@/constants/roles';
import { ADMISSION_TYPES } from '@/constants/student';
import toast from 'react-hot-toast';
import { SiblingSelectionSection } from './SiblingSelectionSection';
import { 
  percentageRegex, 
  textRegex, 
  createRestrictedHandler 
} from '@/utils/inputRestrictions';

export function StepAcademic({ formik, isEditMode = false, initialData = null }) {
  const { values, errors, touched, handleChange, handleBlur } = formik;
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [rollCheck, setRollCheck] = useState({ loading: false, error: false, message: '' });
  const [enableDateEdit, setEnableDateEdit] = useState(false);
  const { schoolId } = useSchool();
  const { user } = useAuth();

  const isOrgAdmin = user?.role === ROLES.ORGANIZATION_ADMIN || user?.role === 'ORGANIZATION_ADMIN' || user?.role === ROLES.SUPER_ADMIN || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!schoolId) return;

    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const response = await academicSessionService.getSessionsBySchool(schoolId);
        const fetchedSessions = Array.isArray(response) ? response : (response.data || []);
        setSessions(fetchedSessions);

        if (!values.academic_session_id && fetchedSessions.length > 0) {
          const current = fetchedSessions.find(s => s.is_current) || fetchedSessions[0];
          formik.setFieldValue('academic_session_id', current.id);
        }
      } catch (err) {
        toast.error('Failed to load academic sessions');
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchSessions();
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId || !values.academic_session_id) return;

    const fetchClasses = async () => {
      setLoading(true);
      try {
        const data = await studentService.getClassesSections();
        setClasses(data.data || []);
      } catch (err) {
        toast.error('Failed to load classes and sections');
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, [schoolId, values.academic_session_id]);

  // Whenever auto_roll_number is checked and school, session, class & section are selected, fetch next roll number
  useEffect(() => {
    async function fetchNextRollNumber() {
      if (
        values.auto_roll_number &&
        schoolId &&
        values.academic_session_id &&
        values.class_id &&
        values.section_id
      ) {
        setRollCheck({ loading: true, error: false, message: 'Generating roll number...' });
        try {
          const res = await studentService.getNextRollNumber({
            school_id: schoolId,
            academic_session_id: values.academic_session_id,
            class_id: values.class_id,
            section_id: values.section_id,
          });
          const nextRoll = res.data?.roll_number || res.roll_number || '';
          if (nextRoll) {
            formik.setFieldValue('roll_number', nextRoll.toString());
            setRollCheck({ loading: false, error: false, message: res.data?.message || 'Auto-generated roll number' });
          } else {
            setRollCheck({ loading: false, error: false, message: 'Auto-generated' });
          }
        } catch {
          setRollCheck({ loading: false, error: false, message: 'Auto-generated by system' });
        }
      }
    }
    fetchNextRollNumber();
  }, [
    values.auto_roll_number,
    schoolId,
    values.academic_session_id,
    values.class_id,
    values.section_id,
  ]);

  const handleRollNumberBlur = async (e) => {
    handleBlur(e);
    const rollVal = e.target.value;
    if (values.auto_roll_number || !rollVal || !schoolId || !values.class_id || !values.section_id || !values.academic_session_id) {
      if (!values.auto_roll_number) {
        setRollCheck({ loading: false, error: false, message: '' });
      }
      return;
    }
    setRollCheck({ loading: true, error: false, message: 'Checking availability...' });
    try {
      const res = await studentService.checkRollNumber({
        school_id: schoolId,
        academic_session_id: values.academic_session_id,
        class_id: values.class_id,
        section_id: values.section_id,
        roll_number: rollVal,
      });
      const isAvailable = res.data?.available ?? res.available ?? true;
      const msg = res.data?.message || res.message || (isAvailable ? 'Roll number is available' : 'Roll number is already taken');

      if (isAvailable) {
        setRollCheck({ loading: false, error: false, message: msg });
      } else {
        setRollCheck({ loading: false, error: true, message: msg });
      }
    } catch (err) {
      setRollCheck({ loading: false, error: true, message: err.response?.data?.message || 'Failed to check roll number availability' });
    }
  };

  const selectedClass = classes.find(c => c.id === values.class_id);
  const sections = selectedClass?.sections || [];
  const selectedSection = sections.find(s => s.id === values.section_id);

  const isCurrentStudentSection = (secId) => {
    if (!isEditMode || !secId) return false;
    return (
      initialData?.enrollment?.section_id === secId ||
      initialData?.student?.section_id === secId ||
      initialData?.section_id === secId
    );
  };

  const isSectionFull = (sec) => {
    if (!sec) return false;
    const cap = Number(sec.capacity) || 0;
    const cur = Number(sec.current_strength) || 0;
    return cap > 0 && cur >= cap;
  };

  const isSelectedSectionFull = Boolean(selectedSection && isSectionFull(selectedSection) && !isCurrentStudentSection(selectedSection.id));
  const availableSections = sections.filter(s => !isSectionFull(s) || isCurrentStudentSection(s.id));

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={sessions}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.session_name || '')}
          value={sessions.find((s) => s.id === values.academic_session_id) || null}
          onChange={(event, newValue) => {
            formik.setFieldValue('academic_session_id', newValue ? newValue.id : '');
          }}
          disabled={true}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => {
            const inputProps = params.InputProps || params.slotProps?.input || {};
            return (
              <TextField
                {...params}
                fullWidth
                label="Academic Session *"
                name="academic_session_id"
                onBlur={handleBlur}
                error={touched.academic_session_id && Boolean(errors.academic_session_id)}
                helperText={touched.academic_session_id && errors.academic_session_id}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...inputProps,
                    endAdornment: (
                      <>
                        {sessionsLoading ? <CircularProgress size={20} /> : null}
                        {inputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box>
          <TextField
            fullWidth
            type="date"
            label="Admission Date *"
            name="admission_date"
            slotProps={{ inputLabel: { shrink: true } }}
            value={values.admission_date}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isEditMode ? (!isOrgAdmin || !enableDateEdit) : false}
            error={touched.admission_date && Boolean(errors.admission_date)}
            helperText={
              isEditMode ? (
                !isOrgAdmin ? (
                  'Only Organization Admin can modify admission date'
                ) : enableDateEdit ? (
                  'Admission date modification enabled'
                ) : (
                  ''
                )
              ) : (
                touched.admission_date && errors.admission_date
              )
            }
          />
          {isEditMode && isOrgAdmin && !enableDateEdit && (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => setEnableDateEdit(true)}
              sx={{ mt: 0.5, textDecoration: 'underline', cursor: 'pointer', display: 'inline-block' }}
            >
              Click here to modify admission date
            </Link>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={ADMISSION_TYPES}
          value={values.admission_type || ''}
          onChange={(event, newValue) => {
            formik.setFieldValue('admission_type', newValue || '');
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Admission Type *"
              name="admission_type"
              onBlur={handleBlur}
              error={touched.admission_type && Boolean(errors.admission_type)}
              helperText={touched.admission_type && errors.admission_type}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={classes}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.class_name || '')}
          value={classes.find((c) => c.id === values.class_id) || null}
          onChange={(event, newValue) => {
            const newClassId = newValue ? newValue.id : '';
            formik.setFieldValue('class_id', newClassId);
            formik.setFieldValue('section_id', '');
          }}
          disabled={loading}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          renderInput={(params) => {
            const inputProps = params.InputProps || params.slotProps?.input || {};
            return (
              <TextField
                {...params}
                fullWidth
                label="Class *"
                name="class_id"
                onBlur={handleBlur}
                error={touched.class_id && Boolean(errors.class_id)}
                helperText={touched.class_id && errors.class_id}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...inputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={20} /> : null}
                        {inputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Autocomplete
          options={sections}
          getOptionLabel={(opt) => {
            if (typeof opt === 'string') return opt;
            const cap = Number(opt.capacity) || 0;
            const cur = Number(opt.current_strength) || 0;
            const isFull = isSectionFull(opt);
            const isCurrent = isCurrentStudentSection(opt.id);
            const statusSuffix = isFull && !isCurrent ? ` (${cur}/${cap} - Full)` : cap > 0 ? ` (${cur}/${cap} seats)` : '';
            return `Section ${opt.section_name || ''}${statusSuffix}`;
          }}
          getOptionDisabled={(opt) => {
            return isSectionFull(opt) && !isCurrentStudentSection(opt.id);
          }}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const cap = Number(option.capacity) || 0;
            const cur = Number(option.current_strength) || 0;
            const isFull = isSectionFull(option);
            const isCurrent = isCurrentStudentSection(option.id);
            const seatsLeft = Math.max(0, cap - cur);

            return (
              <Box
                component="li"
                key={key || option.id}
                {...otherProps}
                sx={{
                  display: 'flex !important',
                  justifyContent: 'space-between !important',
                  alignItems: 'center !important',
                  width: '100%',
                  boxSizing: 'border-box',
                  py: 1,
                  px: 1.5,
                  gap: 1,
                  overflowX: 'hidden',
                  opacity: isFull && !isCurrent ? 0.6 : 1,
                  cursor: isFull && !isCurrent ? 'not-allowed' : 'pointer',
                }}
              >
                {/* Left side: Section Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={700} color={isFull && !isCurrent ? 'text.secondary' : 'text.primary'}>
                    Section {option.section_name}
                  </Typography>
                  {isCurrent && (
                    <Chip label="Current" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
                  )}
                </Box>

                {/* Right side: Capacity & Status (compact & non-wrapping) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {cur}/{cap}
                  </Typography>
                  {isFull && !isCurrent ? (
                    <Chip
                      label="Full"
                      size="small"
                      color="error"
                      sx={{ height: 20, fontSize: '0.675rem', fontWeight: 700, px: 0.5 }}
                    />
                  ) : (
                    <Chip
                      label={`${seatsLeft} left`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.675rem', fontWeight: 600, px: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
            );
          }}
          value={sections.find((s) => s.id === values.section_id) || null}
          onChange={(event, newValue) => {
            formik.setFieldValue('section_id', newValue ? newValue.id : '');
          }}
          disabled={!values.class_id}
          ListboxProps={{
            sx: {
              overflowX: 'hidden !important',
              scrollbarWidth: 'none !important',
              msOverflowStyle: 'none !important',
              '&::-webkit-scrollbar': {
                display: 'none !important',
                width: '0px !important',
                height: '0px !important',
              },
              '& .MuiAutocomplete-option': {
                overflowX: 'hidden !important',
              },
            },
          }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
              sx: {
                '& .MuiPaper-root': {
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0px !important',
                    height: '0px !important',
                  },
                },
                '& .MuiAutocomplete-listbox': {
                  overflowX: 'hidden !important',
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0px !important',
                    height: '0px !important',
                  },
                },
              },
            },
            paper: {
              sx: {
                overflowX: 'hidden !important',
                scrollbarWidth: 'none !important',
                msOverflowStyle: 'none !important',
                '&::-webkit-scrollbar': {
                  display: 'none !important',
                  width: '0px !important',
                  height: '0px !important',
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Section *"
              name="section_id"
              onBlur={handleBlur}
              error={Boolean((touched.section_id && errors.section_id) || isSelectedSectionFull)}
              helperText={
                (touched.section_id && errors.section_id) ||
                (isSelectedSectionFull 
                  ? `Section ${selectedSection.section_name} is full (${selectedSection.current_strength}/${selectedSection.capacity}). ${availableSections.length > 0 ? `Available: ${availableSections.map(s => `Section ${s.section_name}`).join(', ')}` : 'No available sections in this class.'}`
                  : '')
              }
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Box>
          <TextField
            fullWidth
            label={values.auto_roll_number ? 'Roll Number (Auto-Generate)' : 'Roll Number *'}
            name="roll_number"
            value={values.roll_number || ''}
            onChange={handleChange}
            onBlur={handleRollNumberBlur}
            disabled={isEditMode || Boolean(values.auto_roll_number)}
            error={rollCheck.error || (!values.auto_roll_number && touched.roll_number && Boolean(errors.roll_number))}
            helperText={
              isEditMode
                ? 'Roll number cannot be modified in edit mode'
                : values.auto_roll_number
                  ? (rollCheck.message || 'Auto-generated by system')
                  : (rollCheck.message || (touched.roll_number && errors.roll_number) || '')
            }
            slotProps={{
              input: {
                endAdornment: rollCheck.loading ? <CircularProgress size={20} /> : null,
              },
              formHelperText: {
                sx: { color: isEditMode ? 'text.secondary' : (rollCheck.error || (!values.auto_roll_number && touched.roll_number && errors.roll_number)) ? 'error.main' : 'success.main' }
              }
            }}
          />
          {!isEditMode && (
            <FormControlLabel
              control={
                <Checkbox
                  name="auto_roll_number"
                  checked={Boolean(values.auto_roll_number)}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    formik.setFieldValue('auto_roll_number', isChecked);
                    if (!isChecked) {
                      formik.setFieldValue('roll_number', '');
                      setRollCheck({ loading: false, error: false, message: '' });
                    }
                  }}
                  color="primary"
                  size="small"
                />
              }
              label="Roll number auto generate"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="SR Number"
          name="sr_number"
          value={values.sr_number || ''}
          onChange={createRestrictedHandler(handleChange, textRegex)}
          onBlur={handleBlur}
          error={touched.sr_number && Boolean(errors.sr_number)}
          helperText={touched.sr_number && errors.sr_number}
          placeholder="e.g. SR-1024"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Admission Test Marks / (%)"
          name="admission_test_marks"
          value={values.admission_test_marks || ''}
          onChange={createRestrictedHandler(handleChange, percentageRegex)}
          onBlur={handleBlur}
          placeholder="e.g. 85% or 85/100"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Remarks"
          name="remarks"
          value={values.remarks}
          onChange={createRestrictedHandler(handleChange, textRegex, 500)}
          onBlur={handleBlur}
        />
      </Grid>

      {/* Sibling Information Section */}
      <Grid size={{ xs: 12 }}>
        <SiblingSelectionSection formik={formik} schoolId={schoolId} />
      </Grid>
    </Grid>
  );
}
